import React, { useCallback, useMemo, useRef } from 'react';
import { Box, Typography, Row, Col, Form } from '@lucid/lego';
import type { FormRef } from '@lucid/lego';
import type { OrderDetailsResponse } from '../../../../../api/services/lupp/order/dto.order';
import { downloadSfdcFileFromFilehub } from '../../../../../api/services/lupp/fileUpload/fileUpload';
import { formatDate } from '../../../../../utils/tools';
import styles from './Overview.module.scss';
import Configuration from './Configuration/Configuration';
import Customer from './Customer/Customer';
import OrderStatusStepper from './OrderStatusStepper/OrderStatusStepper';
import Summary from './Summary/Summary';
import { groupFilesByCategory } from '../../../../../api/services/lupp/order/transformers.order';
import type { FilesByCategory } from '../../../../../api/services/lupp/order/transformers.order';
import { DOCUMENT_CATEGORY } from '../../../../../constants/constants';
import { createOrderOverviewFormFields } from './formFields';
import { useStore } from '../../../../../store/store';

type OverviewProps = {
  orderData: OrderDetailsResponse;
  filesByCategory?: FilesByCategory;
  theme: 'light' | 'dark';
  labels: Record<string, string>;
  dataTestId?: string;
};

/**
 * Order & Customer Overview section
 * Displays key order information and status timeline
 *
 * @component
 */
const Overview: React.FC<OverviewProps> = ({
  orderData,
  filesByCategory,
  theme,
  labels,
  dataTestId = 'order-overview',
}) => {
  const locale = useStore((state) => state.locale);
  const language = useStore((state) => state.language);
  const formRef = useRef<FormRef>(null);

  // Use pre-grouped map from parent when available; compute as fallback
  const resolvedFilesByCategory = useMemo(
    () => filesByCategory ?? groupFilesByCategory(orderData.moduleFileReferenceDtoList),
    [filesByCategory, orderData.moduleFileReferenceDtoList]
  );

  // Prepare enriched order data from orderData
  const enrichedOrderData = useMemo<OrderDetailsResponse>(() => {
    const appStatus = orderData.financingDetails?.applicationStatus;
    const financingStatus =
      orderData.preferredPaymentMethod?.toLowerCase() === 'cash'
        ? '-'
        : appStatus
          ? appStatus.charAt(0).toUpperCase() + appStatus.slice(1)
          : '-';

    return {
      ...orderData,
      financingStatus,
    };
  }, [orderData]);

  // Compute OFAC display values based on customer type
  const customerOfac = enrichedOrderData?.ofacDetails?.customer;
  const businessOfac = enrichedOrderData?.ofacDetails?.business;
  const normalizedCustomerType = enrichedOrderData?.customerType?.toLowerCase();
  const isCustomerTypeCustomer = normalizedCustomerType === 'private';
  const ofacDetails = isCustomerTypeCustomer ? customerOfac : businessOfac;
  const isOfacCheckComplete = enrichedOrderData?.ofacDetails?.checkCompleted;

  const ofacCheckCompletedLabel = useMemo(() => {
    if (isOfacCheckComplete === true) return labels?.yes ?? 'Yes';
    if (isOfacCheckComplete === false) return labels?.no ?? 'No';
    return '-';
  }, [isOfacCheckComplete, labels?.yes, labels?.no]);

  // Create form fields with default values from enriched order data
  const formFields = useMemo(
    () =>
      createOrderOverviewFormFields(
        labels,
        {
          customerType: enrichedOrderData.customerType || undefined,
          deliveryTimeline: enrichedOrderData.deliveryTimeline || undefined,
          ofacCheckCompleted: ofacCheckCompletedLabel,
          ofacCheckDate: formatDate(ofacDetails?.ofacCheckDate, locale, {}, language ?? undefined),
          ofacStatus: ofacDetails?.ofacStatus || '-',
        },
        {
          disableDeliveryTimeline: enrichedOrderData.orderStatus?.toLowerCase() === 'delivered',
        }
      ),
    [
      labels,
      enrichedOrderData.customerType,
      enrichedOrderData.deliveryTimeline,
      enrichedOrderData.orderStatus,
      ofacCheckCompletedLabel,
      ofacDetails?.ofacCheckDate,
      ofacDetails?.ofacStatus,
      locale,
      language,
    ]
  );

  const handleError = (_error: unknown) => {
    // Validation errors are displayed inline by the Form component
  };

  const orderAgreementFile =
    (resolvedFilesByCategory[DOCUMENT_CATEGORY.ORDER_AGREEMENT] ?? [])[0] ?? null;

  const isFinalInvoiceGenerated = useMemo(
    () =>
      (resolvedFilesByCategory[DOCUMENT_CATEGORY.FINAL_INVOICE] ?? []).some(
        (invoice) => invoice.fileGenerationStatus === 'GENERATED'
      ),
    [resolvedFilesByCategory]
  );

  const handleDownloadAgreement = useCallback(async () => {
    const sfdcDeliveryId =
      orderAgreementFile?.sfdcDeliveryIdentifier ?? enrichedOrderData.sfdcDeliveryIdentifier;
    const sfdcFileId = orderAgreementFile?.sfdcFileIdentifier;

    if (sfdcDeliveryId && sfdcFileId) {
      try {
        await downloadSfdcFileFromFilehub(
          sfdcDeliveryId,
          sfdcFileId,
          orderAgreementFile?.fileNameOriginal || 'order-agreement.pdf'
        );
      } catch (error: unknown) {
        console.error('Failed to download order agreement:', error);
      }
    }
  }, [orderAgreementFile, enrichedOrderData.sfdcDeliveryIdentifier]);

  const customerSection = (
    <Customer
      orderData={enrichedOrderData}
      filesByCategory={resolvedFilesByCategory}
      theme={theme}
      labels={labels || {}}
      orderOverviewFormRef={formRef}
      dataTestId="customer-details"
    />
  );

  return (
    <>
      <Summary
        orderData={enrichedOrderData}
        theme={theme}
        labels={labels}
        locale={locale}
        onDownloadAgreement={handleDownloadAgreement}
        isAgreementAvailable={orderAgreementFile !== null}
        isFinalInvoiceGenerated={isFinalInvoiceGenerated}
        dataTestId={dataTestId}
      />
      <OrderStatusStepper
        orderIdentifier={enrichedOrderData.orderIdentifier}
        orderStatus={enrichedOrderData.orderStatus}
        theme={theme}
        labels={labels}
      />
      <Row gutter={[0, 16]}>
        <Col sm={12} lg={6}>
          <Box className={styles.accordionCard}>
            <Box pt={16} pl={16} pr={16}>
              <Box mt={4}>
                <Typography variant="body" size="large" theme={theme} color="black">
                  {labels?.configurationDetails || 'Configuration Details'}
                </Typography>
              </Box>
            </Box>
            <Box ml={16} mr={16} mt={16} mb={16} pb={32}>
              <Configuration
                orderData={enrichedOrderData}
                theme={theme}
                labels={labels || {}}
                dataTestId="configuration-details"
              />
            </Box>
          </Box>
        </Col>
        <Col sm={12} lg={6}>
          <Form
            id="order-overview-form"
            ref={formRef}
            theme={theme === 'light' ? 'dark' : 'light'}
            formFields={formFields}
            onError={handleError}
            isFluid={true}
            locale={locale}
            language={language ?? undefined}
          />
        </Col>
      </Row>
      <Box>{customerSection}</Box>
    </>
  );
};

Overview.displayName = 'Overview';

export default Overview;
